import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';

import { PlatformEnum, AttributeEnum, ATTRIBUTE_LIST } from '@/types/nftAttribute';
import { mcpEndpointPrefixAtom } from '@/features/spirit/atoms/hosts';
import { currentAccountAtom } from '@/features/identity/atoms';
import { queryMcpLinkedData } from '@/features/spirit/queries';

export const PLATFORM_POINTS: { [key in PlatformEnum]?: number } = {
  [PlatformEnum.ETH]: 3,
  [PlatformEnum.BSC]: 3,
  [PlatformEnum.Kusama]: 3,
  [PlatformEnum.Khala]: 3,
  [PlatformEnum.PolkaDot]: 3,
  [PlatformEnum.Twitter]: 3,
  [PlatformEnum.Discord]: 6,
  [PlatformEnum.Github]: 15
};

const ATTR_RULE = {
  [AttributeEnum.Dexterity]: [
    PlatformEnum.ETH,
    PlatformEnum.BSC,
    PlatformEnum.Kusama,
    PlatformEnum.Khala,
    PlatformEnum.PolkaDot
  ],
  [AttributeEnum.Willpower]: [
    PlatformEnum.Kusama,
    PlatformEnum.Khala,
    PlatformEnum.PolkaDot,
    PlatformEnum.Twitter,
    PlatformEnum.Discord
  ],
  [AttributeEnum.Intelligent]: [PlatformEnum.Github],
  [AttributeEnum.Strength]: [
    PlatformEnum.ETH,
    PlatformEnum.BSC,
    PlatformEnum.Kusama,
    PlatformEnum.Khala,
    PlatformEnum.PolkaDot
  ]
};

type valueType = {
  currentValue: number;
  successValue: number;
};

const initialValue = {
  currentValue: 0,
  successValue: 100
};

const initialResult = {
  [AttributeEnum.Dexterity]: initialValue,
  [AttributeEnum.Willpower]: initialValue,
  [AttributeEnum.Intelligent]: initialValue,
  [AttributeEnum.Strength]: initialValue
};

/**
 * https://docs.google.com/spreadsheets/d/1tacgzDrfLQDU4EWJ_-gWzQ8Uzg8PddqWRWAUJ-YV5Rw/edit#gid=451796984
 * @returns
 */
export const useAttrEstimatedValue = (): {
  [key in AttributeEnum]: valueType;
} => {
  const mcpPrefix = useAtomValue(mcpEndpointPrefixAtom);
  const account = useAtomValue(currentAccountAtom);
  const { data } = useQuery(queryMcpLinkedData(mcpPrefix, account?.address || ''));

  return useMemo(() => {
    const res = initialResult;
    if (data) {
      ATTRIBUTE_LIST.forEach((attr) => {
        const successValue = ATTR_RULE[attr].reduce((acc: number, platform) => {
          if (PLATFORM_POINTS[platform]) {
            return acc + (PLATFORM_POINTS[platform] as number);
          }
          return acc;
        }, 0);

        const currentValue = ATTR_RULE[attr].reduce((acc: number, platform) => {
          if (platform in data && PLATFORM_POINTS[platform]) {
            return acc + (PLATFORM_POINTS[platform] as number);
          }
          return acc;
        }, 0);

        res[attr] = { currentValue, successValue };
      });
    }

    return res;
  }, [data]);
};
